// CO - Kit montado — comportamento do bloco conso-kit-montado.liquid.
//
// Cada slot tem uma fileira de cores e uma de tamanhos (ver o snippet
// conso-kit-montado-slot). O par (cor, tamanho) -> id de variante viaja no
// `data-tam` da cor escolhida, então trocar de cor é o que redefine quais
// tamanhos existem — e não o contrário.
//
// As properties existem para a function `kit-montado` do app consolatio-discounts:
//   _conso_kitm    (em todas as linhas)  token do kit
//   _conso_kitm_n  (só na linha do tênis) total de unidades de peça do kit
//
// A function só zera o tênis quando a soma das unidades não-brinde com aquele token
// bate com `_conso_kitm_n`. Sem isso o cliente removeria as peças da sacola e ficaria
// com o tênis de graça — o pareamento por id de variante da `kit-tenis` não cobre
// este fluxo porque aqui não existe linha do kit no carrinho.
//
// O `cart:update` vai SEM `window.__consoSilentCartUpdate`: isto é um add-to-cart do
// cliente, o drawer deve abrir (ver assets/conso-cart-drawer-guard.js).

if (!window.__consoKitMontadoInit) {
  window.__consoKitMontadoInit = true;

  const SOURCE_ID = 'conso-kit-montado';
  const ERRO_GENERICO = 'Não foi possível adicionar o kit ao carrinho. Tente novamente.';
  const ADDED_RESET_MS = 1000;

  const cartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  const getCartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((sectionId, index, all) => all.indexOf(sectionId) === index);

  /** "P:123~M:456" -> Map { P => 123, M => 456 }. Sem tamanho, a chave é "". */
  const mapaDeTamanhos = (bruto) => {
    const mapa = new Map();

    for (const par of (bruto || '').split('~')) {
      if (!par) continue;
      const corte = par.lastIndexOf(':');
      if (corte < 0) continue;

      const id = Number(par.slice(corte + 1));
      if (id) mapa.set(par.slice(0, corte), id);
    }

    return mapa;
  };

  /** Compara nome de cor do metafield com o da variante sem tropeçar em acento/caixa. */
  const normalizar = (valor) =>
    (valor || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');

  class ConsoKitMontado extends HTMLElement {
    #inFlight = false;
    #resetTimer = null;
    #faixaLooks = null;

    connectedCallback() {
      if (this.__consoBound) return;
      this.__consoBound = true;

      this.addEventListener('change', this.#handleChange);
      this.addEventListener('click', this.#handleClick);

      for (const slot of this.#slots) this.#sincronizarSlot(slot);

      this.#instalarLooks();
    }

    get #slots() {
      return Array.from(this.querySelectorAll('[data-km-slot]'));
    }

    #corDe(slot) {
      return slot.querySelector('[data-km-cor]:checked');
    }

    /** Reflete a cor escolhida: miniatura, nome e quais tamanhos existem nela. */
    #sincronizarSlot(slot) {
      const cor = this.#corDe(slot);
      if (!cor) return;

      const thumb = slot.querySelector('[data-km-thumb]');
      if (thumb && cor.dataset.thumb) thumb.src = cor.dataset.thumb;

      const nome = slot.querySelector('[data-km-cor-nome]');
      if (nome) nome.textContent = cor.value;

      // Tamanho fora de estoque naquela cor sai de circulação em vez de sumir:
      // a fileira muda de largura a cada troca de cor se os botões forem removidos,
      // e o cliente perde a referência de onde clicar.
      const mapa = mapaDeTamanhos(cor.dataset.tam);

      for (const input of slot.querySelectorAll('[data-km-tam]')) {
        const existe = mapa.has(input.value);

        input.disabled = !existe;
        input.closest('.km__tam')?.classList.toggle('km__tam--off', !existe);

        // A cor nova não tem o tamanho que estava marcado: melhor perder a escolha
        // do que mandar pro carrinho uma variante que não é a que está na tela.
        if (!existe && input.checked) input.checked = false;
      }

      slot.classList.remove('km__slot--erro');
    }

    #handleChange = (event) => {
      const cor = event.target.closest('[data-km-cor]');
      if (cor) {
        const slot = cor.closest('[data-km-slot]');
        if (slot) this.#sincronizarSlot(slot);
        // Mexeu numa cor na mão: a combinação já não é mais a do look.
        this.#marcarLook(null);
        this.#setError('');
        return;
      }

      const tamanho = event.target.closest('[data-km-tam]');
      if (tamanho) {
        tamanho.closest('[data-km-slot]')?.classList.remove('km__slot--erro');
        this.#setError('');
      }
    };

    #handleClick = (event) => {
      if (!event.target.closest('[data-km-add]')) return;
      event.preventDefault();
      this.#add();
    };

    // --- Looks -------------------------------------------------------------

    #galeria() {
      const escopo = this.closest('.conso-kit') || this.closest('.shopify-section') || document;
      return escopo.querySelector('media-gallery');
    }

    /**
     * A faixa nasce dentro do bloco e é movida pra dentro da galeria, onde o CSS
     * do `conso-kit-switch` a ancora na base da foto — mesmo lugar em que o
     * cliente já via as combinações quando cada uma era um produto-bundle.
     * Sem galeria na página ela fica onde nasceu, em fluxo normal.
     */
    #instalarLooks() {
      const faixa = this.querySelector('[data-km-looks]');
      if (!faixa) return;

      this.#faixaLooks = faixa;

      const galeria = this.#galeria();
      if (galeria) {
        galeria.classList.add('km-com-looks');
        galeria.appendChild(faixa);
      } else {
        faixa.classList.add('conso-kit-switch--inline');
      }

      // Depois do move a faixa está fora deste elemento, então a delegação do
      // `this` não a alcança mais — o listener é dela.
      faixa.addEventListener('click', (event) => {
        const botao = event.target.closest('.conso-kit-switch__item');
        if (botao) this.#aplicarLook(botao);
      });

      faixa.hidden = false;
    }

    #marcarLook(ativo) {
      const faixa = this.#faixaLooks;
      if (!faixa) return;

      for (const botao of faixa.querySelectorAll('.conso-kit-switch__item')) {
        botao.setAttribute('aria-pressed', String(botao === ativo));
      }

      const legenda = faixa.querySelector('[data-km-look-nome]');
      if (legenda) legenda.textContent = ativo ? ativo.getAttribute('aria-label') || '' : '';
    }

    /**
     * Escreve só a COR de cada slot, na ordem em que eles aparecem. O tamanho
     * continua sendo do cliente — é o que permite ver os looks depois de já ter
     * escolhido os tamanhos sem perder o que preencheu.
     *
     * Cor que não existe naquela peça é ignorada em silêncio: se alguém editar o
     * `kit_pecas` sem mexer no `kit_looks`, o pior caso é o look não aplicar em
     * uma peça — e não aplicar a cor errada na peça errada.
     */
    #aplicarLook(botao) {
      const cores = (botao.dataset.kmLook || '').split('|');
      const slots = this.#slots;

      cores.forEach((nome, indice) => {
        const alvo = normalizar(nome);
        if (!alvo) return;

        const slot = slots[indice];
        if (!slot) return;

        const opcao = Array.from(slot.querySelectorAll('[data-km-cor]')).find(
          (input) => normalizar(input.value) === alvo
        );
        if (!opcao) return;

        opcao.checked = true;
        this.#sincronizarSlot(slot);
      });

      this.#marcarLook(botao);
      this.#setError('');

      const foto = botao.dataset.kmFoto;
      if (foto != null) this.#galeria()?.slideshow?.select(Number(foto));
    }

    // --- Carrinho ----------------------------------------------------------

    #setLoading(loading) {
      const button = this.querySelector('[data-km-add]');
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.classList.toggle('km--loading', loading);
    }

    #setError(message) {
      const box = this.querySelector('[data-km-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    #animate() {
      if (this.dataset.animarAdd === 'false') return;

      const button = this.querySelector('[data-km-add]');
      if (!button) return;

      button.dataset.added = 'true';
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => button.removeAttribute('data-added'), ADDED_RESET_MS);

      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = this.#slots[0] ? this.#corDe(this.#slots[0])?.dataset.thumb : null;
      if (!cartIcon || !image) return;

      const flyToCart = document.createElement('fly-to-cart');
      flyToCart.classList.add('fly-to-cart--main');
      flyToCart.style.setProperty('background-image', `url(${image})`);
      flyToCart.style.setProperty('--start-opacity', '0');
      flyToCart.source = button;
      flyToCart.destination = cartIcon;
      document.body.appendChild(flyToCart);
    }

    // Uma linha por variante distinta: escolher a mesma cor e o mesmo tamanho em
    // dois slots vira quantity 2, e não duas linhas iguais na sacola. Peça e brinde
    // nunca se fundem porque as properties diferem — e é o que mantém o
    // `_conso_kitm_n` só no tênis.
    #buildItems() {
      const token = this.dataset.kmToken;
      const unidades = this.dataset.kmUnidades;
      const items = [];
      const faltando = [];

      for (const slot of this.#slots) {
        const cor = this.#corDe(slot);
        const mapa = mapaDeTamanhos(cor?.dataset.tam);
        const tamanhos = slot.querySelectorAll('[data-km-tam]');

        let id = null;
        if (tamanhos.length === 0) {
          // Peça de uma opção só: a cor já resolve a variante.
          id = mapa.values().next().value ?? null;
        } else {
          const escolhido = slot.querySelector('[data-km-tam]:checked');
          if (escolhido) id = mapa.get(escolhido.value) ?? null;
        }

        if (!id) {
          faltando.push(slot);
          continue;
        }

        const brinde = slot.hasAttribute('data-km-brinde');
        const properties = brinde
          ? { _conso_kitm: token, _conso_kitm_n: unidades }
          : { _conso_kitm: token };

        const existing = items.find(
          (item) => item.id === id && JSON.stringify(item.properties) === JSON.stringify(properties)
        );
        if (existing) existing.quantity += 1;
        else items.push({ id, quantity: 1, properties });
      }

      return { items, faltando };
    }

    /** Aponta as peças sem tamanho em vez de mandar meio kit pro carrinho. */
    #cobrarTamanhos(faltando) {
      for (const slot of faltando) slot.classList.add('km__slot--erro');

      const nomes = faltando.map((slot) => slot.dataset.kmNome).filter(Boolean);
      const lista = nomes.join(', ');

      this.#setError(
        nomes.length > 1 ? `Escolha o tamanho de: ${lista}.` : `Escolha o tamanho: ${lista}.`
      );

      faltando[0]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      faltando[0]?.querySelector('[data-km-tam]:not(:disabled)')?.focus({ preventScroll: true });
    }

    async #add() {
      if (this.#inFlight) return;

      const { items, faltando } = this.#buildItems();

      if (faltando.length > 0) {
        this.#cobrarTamanhos(faltando);

        // Nada entrou no carrinho. O `conso-kit-upsell.js` arma no clique do
        // botão e segura o drawer até vir um `cart:update`; sem este aviso a
        // sacola ficaria muda até o teto de 8s dele expirar.
        document.dispatchEvent(new CustomEvent('cart:error', { bubbles: true }));
        return;
      }

      if (items.length === 0) return;

      this.#inFlight = true;
      this.#setLoading(true);
      this.#setError('');

      try {
        const body = { items };
        const sectionIds = getCartSectionIds();
        if (sectionIds.length > 0) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const payload = await response.json();

        // `/cart/add.js` devolve 422 com `description` quando uma variante ficou sem
        // estoque entre o render da página e o clique — caso real num kit, que leva
        // minutos de escolha. A chamada é atômica, então nada entrou.
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || ERRO_GENERICO);
        }

        this.#animate();

        const sections = payload.sections && typeof payload.sections === 'object' ? payload.sections : undefined;

        const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: SOURCE_ID,
              data: {
                itemCount: Number(cart?.item_count ?? 0),
                source: SOURCE_ID,
                sections,
              },
            },
          })
        );
      } catch (error) {
        const mensagem = error?.message || ERRO_GENERICO;
        this.#setError(mensagem);

        // Mesmo aviso do caminho de tamanho faltando, pelo mesmo motivo: nada
        // entrou no carrinho. Sem ele o `conso-kit-upsell.js` segura a sacola
        // até o teto de 8s e o `conso-kit-tracking.js` não fica sabendo que a
        // adição morreu no servidor (variante que esgotou entre o render e o
        // clique — o kit leva minutos de escolha).
        //
        // `message`/`errors`/`description` ficam FORA do detail de propósito:
        // são as chaves que o `gift-card-recipient-form.js` do tema lê pra
        // pintar erro na tela dele, sem filtrar por origem.
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { sourceId: SOURCE_ID, data: { source: SOURCE_ID, kmErro: mensagem } },
          })
        );
      } finally {
        this.#inFlight = false;
        this.#setLoading(false);
      }
    }
  }

  if (!customElements.get('conso-kit-montado')) {
    customElements.define('conso-kit-montado', ConsoKitMontado);
  }
}
